import Modal from "../../Modal/Modal";
import React, { FormEvent, useState } from "react";
import {
  AddressElement,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { AddPaymentMethod } from "../../../services/payment.service";
import useAuthService from "../../../auth/AuthService";
import Button from "../../Button/Button";

interface CardProps {
  id: string;
  title: string;
  onClick?: () => void;
  onClose?: () => void;
  onBack?: (error?: string) => void;
  isVisible: boolean;
  isAuction?: boolean;
  billingAddress: BillingDetails;
  handleBillingDetails: (stripeAddress: any) => void;
  addcardState?: any;
}

interface BillingDetails {
  name: string;
  email: string;
  phone: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

const AddCardBillingAddress: React.FC<CardProps> = ({
  id,
  title,
  onClick,
  onClose,
  isVisible,
  onBack,
  isAuction = false,
  billingAddress,
  handleBillingDetails,
  addcardState,
}) => {
  const { getToken } = useAuthService();
  const stripe = useStripe();
  const elements = useElements();

  const [state, setState] = useState({
    isLoading: false,
    error: "",
    addressComplete: false,
  });

  const handleToggle = (key: keyof typeof state, value: boolean) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!state.addressComplete) {
      setState((prev) => ({
        ...prev,
        error:
          "Please enter a valid billing address, including a valid postal code.",
      }));
      return;
    }

    if (!stripe || !elements) {
      setState((prev) => ({
        ...prev,
        error: "Stripe.js has not loaded yet",
        isLoading: false,
      }));
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      setState((prev) => ({
        ...prev,
        error: "Please complete all fields.",
        isLoading: false,
      }));
      return;
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: billingAddress,
      });

      if (error) {
        setState((prev) => ({
          ...prev,
          error:
            error?.message ||
            "An error occurred while creating the payment method.",
        }));
      } else {
        handleToggle("isLoading", true);
        const response = await AddPaymentMethod(
          {
            payment_method_id: paymentMethod?.id,
            set_as_default: isAuction ? true : addcardState.isDefault,
          },
          getToken
        );
        if (response.error === null && response.data.status_code === 200) {
          if (onClick) {
            onClick();
          }
        } else {
          if (
            typeof response.error?.data?.error === "string" &&
            response.error.data.error.includes("card")
          ) {
            onBack && onBack(response.error.data.error);
          } else {
            setState((prev) => ({
              ...prev,
              error: response.error.data.error,
            }));
          }
        }
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Something went wrong. Please try again.",
      }));
      console.error(error);
    } finally {
      handleToggle("isLoading", false);
    }
  };

  const modalBody = (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : null}

      <div className="cart-modal modal-body bg-modalbody text-dark-purple pt-0">
        <form className="account-form text-start" onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col mb-3 address-element">
              <AddressElement
                options={{
                  mode: "billing",
                  display: { name: "full" },
                  fields: {
                    phone: "never",
                  },
                  defaultValues: billingAddress || {},
                }}
                onChange={(event) => {
                  const isComplete = event.complete;

                  setState((prev) => ({
                    ...prev,
                    addressComplete: isComplete,
                    error: isComplete ? "" : prev.error,
                  }));

                  if (isComplete || event.isNewAddress) {
                    const stripeAddress: any = event.value;
                    handleBillingDetails(stripeAddress);
                  }
                }}
              />
            </div>
          </div>

          {state.error && <p style={{ color: "red" }}>{state.error}</p>}

          <Button
            label={state.isLoading ? "Processing..." : "Save Card"}
            className="w-100 bg-light-purple mt-2 font-inter-500-18"
            type="submit"
            disabled={state.isLoading}
          />

          <Button
            label={
              <>
                <i className="fa-solid fa-arrow-left"></i> Back to card
              </>
            }
            className="w-100 bg-light-purple mt-3 mb-3 font-inter-500-18 toLeft"
            onClick={() => onBack && onBack()}
          />
        </form>
      </div>
    </>
  );

  return (
    <Modal
      id={id}
      title={title}
      body={modalBody}
      modalBackground={"modal-bg"}
      onClose={onClose}
      isVisible={isVisible}
    />
  );
};

export default AddCardBillingAddress;
